import { useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { Disc3, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import BetControl from "../components/BetControl";
import Confetti from "../components/Confetti";
import CoinRain from "../components/CoinRain";
import { formatCoins } from "../lib/format";
import { playSpin, playWin, playLose } from "../lib/sound";

const SEG = 8;
const SLICE = 360 / SEG;
const COLORS = ["#FFD700", "#2b2130", "#00F0FF", "#00E676", "#2b2130", "#FF007F", "#2b2130", "#9D4EDD"];

function slicePath(i, r = 100, cx = 110, cy = 110) {
  const a0 = (i * SLICE - 90) * (Math.PI / 180);
  const a1 = ((i + 1) * SLICE - 90) * (Math.PI / 180);
  const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
  return `M${cx},${cy} L${x0},${y0} A${r},${r} 0 0 1 ${x1},${y1} Z`;
}

export default function SpinWheel() {
  const { t } = useLang();
  const { user, setCoins } = useAuth();
  const controls = useAnimation();
  const [segments, setSegments] = useState([]);
  const [bet, setBet] = useState(100);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [confetti, setConfetti] = useState(0);
  const [rain, setRain] = useState(0);
  const [auto, setAuto] = useState(false);
  const [jackpot, setJackpot] = useState(false);
  const rotationRef = useRef(0);
  const autoRef = useRef(false);
  const betRef = useRef(bet);
  betRef.current = bet;

  useEffect(() => {
    api.get("/games/spin/config").then(({ data }) => setSegments(data.segments)).catch(() => {});
    return () => { autoRef.current = false; };
  }, []);

  const animateTo = async (index) => {
    const targetCenter = index * SLICE + SLICE / 2;
    const base = rotationRef.current - (rotationRef.current % 360);
    const final = base + 360 * 6 + (360 - targetCenter);
    rotationRef.current = final;
    await controls.start({ rotate: final, transition: { duration: 4.2, ease: [0.17, 0.67, 0.12, 0.99] } });
  };



  const spin = async () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);
    playSpin();
    try {
      const { data } = await api.post("/games/spin", { bet: betRef.current });
      const targetCenter = data.index * SLICE + SLICE / 2;
      const base = rotationRef.current - (rotationRef.current % 360);
      const final = base + 360 * 6 + (360 - targetCenter);
      rotationRef.current = final;
      await controls.start({
        rotate: final,
        transition: { duration: 4.2, ease: [0.17, 0.67, 0.12, 0.99] },
      });
      setCoins(data.coins);
      setResult(data);
      if (data.win > 0) {
        setConfetti(Date.now());
        setRain(Date.now());
        playWin(data.win);
        if (data.jackpot) {
          setJackpot(true);
          setTimeout(() => setJackpot(false), 3000);
          toast.success(`🎉 ${t("jackpot")} +${formatCoins(data.win)} ${t("coins")}!`);
        } else {
          toast.success(`${t("youWon")} +${formatCoins(data.win)} ${t("coins")}!`);
        }
      } else {
        playLose();
        toast(`${t("youLost")} (-${formatCoins(betRef.current)})`);
      }
      setSpinning(false);
      if (autoRef.current) {
        if (data.coins >= betRef.current) {
          setTimeout(() => { if (autoRef.current) spin(); }, 1100);
        } else {
          autoRef.current = false;
          setAuto(false);
          toast("Auto stopped · पर्याप्त ₹ नहीं / Not enough balance");
        }
      }
    } catch (err) {
      autoRef.current = false;
      setAuto(false);
      setSpinning(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleAuto = () => {
    const next = !auto;
    setAuto(next);
    autoRef.current = next;
    if (next && !spinning) spin();
  };

  const labels = segments.length ? segments : Array.from({ length: SEG }, () => ({ label: "" }));

  return (
    <div className={`space-y-6 ${jackpot ? "animate-shake" : ""}`}>
      <Confetti fire={confetti} />
      <CoinRain fire={rain} />
      {jackpot && (
        <div className="fixed inset-0 z-[100] grid place-items-center pointer-events-none" data-testid="jackpot-overlay">
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative animate-jackpot text-center">
            <div className="font-display text-6xl sm:text-8xl font-extrabold gold-text drop-shadow-[0_0_30px_rgba(255,215,0,0.8)]">
              {t("jackpot")}
            </div>
            <p className="mt-2 text-xl font-bold text-amber-200">{t("jackpotMsg")}</p>
          </div>
        </div>
      )}
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">{t("spinWheel")}</h1>
        <p className="text-sm text-slate-400 mt-1">1.5x – 100x · {t("jackpot")} 🎰 · {t("howToPlay")}: {t("bet")} → {t("spin")}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="mw-card p-6 flex flex-col items-center">
          <div className="relative" style={{ width: 240, height: 240 }}>
            <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-20"
                 style={{ width: 0, height: 0, borderLeft: "14px solid transparent", borderRight: "14px solid transparent", borderTop: "24px solid #FFD700", filter: "drop-shadow(0 0 6px rgba(255,215,0,0.8))" }} />
            <motion.svg
              width="240" height="240" viewBox="0 0 220 220"
              animate={controls}
              style={{ filter: "drop-shadow(0 0 24px rgba(255,215,0,0.25))" }}
              data-testid="spin-wheel-svg"
            >
              <circle cx="110" cy="110" r="106" fill="none" stroke="#FFD700" strokeWidth="4" />
              {labels.map((s, i) => {
                const mid = (i * SLICE + SLICE / 2 - 90) * (Math.PI / 180);
                const tx = 110 + 66 * Math.cos(mid);
                const ty = 110 + 66 * Math.sin(mid);
                return (
                  <g key={i}>
                    <path d={slicePath(i)} fill={COLORS[i]} stroke="#070B1F" strokeWidth="1.5" />
                    <text x={tx} y={ty} fill="#fff" fontSize="15" fontWeight="800"
                          textAnchor="middle" dominantBaseline="middle"
                          transform={`rotate(${i * SLICE + SLICE / 2}, ${tx}, ${ty})`}>
                      {s.label}
                    </text>
                  </g>
                );
              })}
              <circle cx="110" cy="110" r="20" fill="#070B1F" stroke="#FFD700" strokeWidth="3" />
            </motion.svg>
          </div>

          <button
            onClick={spin}
            disabled={spinning}
            data-testid="spin-wheel-btn"
            className="mt-6 btn-gold rounded-full px-10 py-3 text-lg flex items-center gap-2 animate-pulse-glow disabled:animate-none"
          >
            {spinning ? <Loader2 className="animate-spin" size={20} /> : <Disc3 size={20} />}
            {spinning ? t("spinning") : t("spin")}
          </button>

          <button
            onClick={toggleAuto}
            data-testid="auto-spin-btn"
            className={`mt-3 flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold border transition-colors ${auto ? "border-rose-500/60 bg-rose-500/15 text-rose-300" : "border-cyan-400/40 bg-black/30 text-cyan-300 hover:border-cyan-400/80"}`}
          >
            <RefreshCw size={15} className={auto ? "animate-spin" : ""} />
            {auto ? t("stopAuto") : t("autoSpin")}
          </button>

        </div>

        <div className="mw-card p-6 space-y-5">
          <BetControl bet={bet} setBet={setBet} max={user?.coins} disabled={spinning} />
          {result && (
            <div className={`rounded-xl p-4 text-center border ${result.win > 0 ? "border-emerald-500/40 bg-emerald-500/10" : "border-rose-500/30 bg-rose-500/10"}`} data-testid="spin-result">
              <p className="text-xs uppercase tracking-wider text-slate-400">{t("result")}</p>
              <p className="font-display text-3xl font-extrabold mt-1" style={{ color: result.win > 0 ? "#00E676" : "#FF3366" }}>
                {result.multiplier}× · {result.win > 0 ? "+" : ""}{formatCoins(result.net)}
              </p>
            </div>
          )}
          <div className="grid grid-cols-5 gap-2 text-center text-xs">
            {["1.5x","2x","3x","5x","100x"].map((m) => (
              <div key={m} className={`rounded-lg border py-2 font-mono-num ${m === "100x" ? "border-amber-400/60 bg-amber-500/10 gold-text font-bold" : "border-amber-500/20 bg-black/30 text-amber-200"}`}>{m}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
